const countThirdLeg = (customers) => {
  // Count the number of partners and members in the third leg that have OV
  return customers.reduce((counts, customer) => {
    const customerType = customer.customer?.type;
    if (customerType === "Associate" && customer.customer?.ov > 0) {
      counts.partners++;
    } else if (customerType === "Member" && customer.customer?.ov > 0) {
      counts.members++;
    }
    return counts;
  }, { partners: 0, members: 0 });
};

// NOTE: the 'profilePicture' function parameter could be a javascript string, "" or new String(), or a javascript object {}
// When it is a javascript string then it can be an JSON object string with the sizes array or a direct s3://unicitystatic URL
const parseProfilePictureUrl = (profilePicture) => {
  if (profilePicture) {
    if (Object.prototype.hasOwnProperty.call(profilePicture, "sizes") && profilePicture.sizes && profilePicture.sizes.length > 0 && profilePicture.sizes[0].media) {
      return profilePicture.sizes[0].media.replace("s3://unicitystatic", process.env.REACT_APP_UNICITY_STATIC_URL);
    } else if (profilePicture instanceof String || typeof profilePicture === "string") {
      if (profilePicture.startsWith("s3://unicitystatic")) {
        return profilePicture.replace("s3://unicitystatic", process.env.REACT_APP_UNICITY_STATIC_URL);
      } else {
        try {
          const profPic = JSON.parse(profilePicture);
          if (Object.prototype.hasOwnProperty.call(profPic, "sizes") && profPic.sizes && profPic.sizes.length > 0 && profPic.sizes[0].media) {
            return profPic.sizes[0].media.replace("s3://unicitystatic", process.env.REACT_APP_UNICITY_STATIC_URL);
          } else {
            return "";
          }
        } catch (err) {
          console.log(err);
          return "";
        }
      }
    } else {
      return "";
    }
  } else {
    return "";
  }
}

const isDate60DaysInactive = (date) => {
  if (date === null) return true;
  const today = new Date();
  const orderDate = new Date(date);
  const differenceInTime = orderDate.getTime() - today.getTime();
  const differenceInDays = differenceInTime / (1000 * 3600 * 24);
  return differenceInDays > 60;
}

function getTruncatedName(firstName, lastName, truncateName, truncatePosition = 15) {
  // Concatenate the first name and last name to get length
  const name = `${firstName} ${lastName}`;

  // Calculate the length
  const nameLength = name.length;

  // Conditionally truncate the name based on the name length and truncate position value
  const nameTruncated = nameLength > truncatePosition && truncateName
    ? `${name.slice(0, truncatePosition)}...` // Truncate and add ellipsis if length exceeds position
    : name; // Use full name otherwise

  // Split the truncated name
  const nameTruncatedSplit = nameTruncated.split(" ");

  // Extract the first and last name from the truncated name
  const [firstNameTruncated, ...lastNameParts] = nameTruncatedSplit;
  const lastNameTruncated = lastNameParts.length > 0 ? lastNameParts.join(' ') : '';

  return { firstNameTruncated, lastNameTruncated };
}
  
const calculateDynamicRowHeight = (params) => {
  let dynamicRowHeight = 50;
  let chipCount = 0;

  const isSuspended = params.model.customerStatus === "Suspended";
  const isInactivePartner = params.model.type === "partner" && !params.model.is100PvActive;
  const isInactiveMember = params.model.type === "member" && isDate60DaysInactive(params.model.lastOrderDate);
  const isMember = params.model.type === "member";
  const hasNativeName = !!params.model.nativeName;

  // UI renders either a suspended chip or inactive chip, see PartnersNameCell.jsx: 102
  if (isSuspended || isInactivePartner || isInactiveMember) chipCount += 1;
  if (isMember) chipCount += 1;

  if (chipCount > 0) dynamicRowHeight += chipCount * 15.5;
  if (hasNativeName) dynamicRowHeight += 14;

  return dynamicRowHeight;
}

const formatNumber = (num, locale = 'en-US') => num.toLocaleString(locale)

/**
 * @param {param} productData the raw product data from the endpoint, sources
 * can be either "uShop" "cosmic" or "drupal"
 * @param {param} source an integer value that represents the source of the product data
 * 1: Cosmic 
 * 2: UShop
 * 3: Drupal
 * @param {param} locale the locale of the product data, default is "english"
 * NOTE: not all sources have all locales, so default to english
 */
const transformProductData = (productData, source, _locale = "english") => {
  const transformers = {
    1: (product) => {
      // remove all variants that does not have a sku
      const filteredVariations = product?.variations?.filter(variation => variation.sku);
      return {
        productName: product.name,
        id: product._id,
        sku: filteredVariations.length === 1
          ? filteredVariations[0]?.sku || ""
          : filteredVariations.find(variation => variation.isDefault)?.sku || "",
        imageUrl: product.image.imageUrl || "",
        slug: product.slug
      }
    },
    2: (product) => ({
      productName: product.item_name.english,
      id: product.id,
      sku: product.item_code,
      imageUrl: product.image_url || ""
    }),
    3: (product) => ({
      productName: product.content.name,
      id: parseInt(product.item.id.unicity),
      sku: product.item.id.unicity,
      imageUrl: product.content.images ? product.content.images[0].styles.medium : ""
    })
  };

  const transformer = transformers[source] || transformers[1];

  return productData
    .map(transformer)
    .sort((a, b) => a.productName.localeCompare(b.productName));
};

/**
 * Get the country information and the source value
 * @param {string} country - The country code
 * @param {object} referralLinkConfig - The referral link config from launchdarkly
 * @param {array} countries - The countries array from launchdarkly
 * @returns {object} - The country information and the source value
 */
const getCountryInformation = (country, referralLinkConfig, countries) => {
  const countryInfo = countries.find((c) => c.code.toLowerCase() === country.toLowerCase());
  const sourceValue = referralLinkConfig[country.toLowerCase()];
  return { ...countryInfo, source: sourceValue };
}

// GO-2724: confirmed against live QA data. `customers?unicity=X&expand=
// customer` (MemberContext's memberData, PartnersContext's promise2) never
// includes sponsor data at all, regardless of field path -- only
// `{href}?expand=profilePicture` (MemberContext's customerHrefData) does, at
// sponsor.id.unicity. That field comes back as a NUMBER while ids elsewhere
// (URL params, userInfo.unicityId) are strings -- coerce once here so every
// downstream consumer (access checks, redirect comparisons, redirect URL
// construction) works off the same already-stringified value.
const getSponsorUnicityId = (customerRecord) => {
  return customerRecord?.sponsor?.id?.unicity?.toString();
}

// GO-2724: confirmed (Jacob, direct) distributor-indicating type values are
// "Associate"/"Partner"/"Distributor", case-insensitive -- anything else is
// a plain member. Used to categorize both the row being rendered and the
// path distributor whose page it's on, so clickability can require the two
// to match (distributor rows clickable only under a distributor page,
// member rows clickable only under a member page).
const DISTRIBUTOR_TYPES = ["associate", "partner", "distributor"];
const getCustomerCategory = (rawType) => {
  return DISTRIBUTOR_TYPES.includes((rawType || "").toLowerCase()) ? "partner" : "member";
}

export {
  countThirdLeg,
  parseProfilePictureUrl,
  isDate60DaysInactive,
  getTruncatedName,
  calculateDynamicRowHeight,
  formatNumber,
  transformProductData,
  getCountryInformation,
  getSponsorUnicityId,
  getCustomerCategory
}